import { FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Props, State } from "./interfaces";
import { DTOModel } from "../../models/base/dto-model";

const CustomFetchingDropdown = <T extends DTOModel>({
  apiService,
  fieldProp,
  selectedValue,
  label,
  handleChangeParent,
  propsFilter,
  childFields,
  customInputStyle,
  taggedMenuOption,
}: Props<T>) => {
  const [state, setState] = useState<State>({
    items: [],
    totalItems: 0,
    loading: false,
    page: {
      pageSize: 10,
      pageNumber: 0,
    },
  });

  //#region change
  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      const _selectedValue = state.items.find((item) => item?.id === event.target.value) ?? null;

      handleChangeParent(_selectedValue);
    },
    [state.items]
  );
  //#endregion

  //#region load data
  const loadData = useCallback(
    async (signal: AbortSignal) => {
      try {
        setState((state) => ({ ...state, loading: true }));
        const response = await apiService.loadItems(
          {
            ...propsFilter,
          },
          undefined,
          [],
          state.page,
          undefined,
          60,
          signal
        );
        if (!(response.error || signal.aborted)) {
          setState((state) => ({
            ...state,
            items: [...state.items, ...response.items.filter((_item) => _item.id !== selectedValue?.id)],
            totalItems: response.totalItems,
          }));
        }
      } catch (error) {
        console.log("Error loading data:", error);
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [state.page, state.items, apiService, propsFilter, selectedValue]
  );
  //#endregion

  //#region change page
  const handleChangePage = (pageNumber: number) => {
    setState(() => ({
      ...state,
      page: {
        ...state.page,
        pageNumber,
      },
    }));
  };
  //#endregion

  useEffect(() => {
    const abortController = new AbortController();
    loadData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [state.page]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { page, totalItems } = state;
    const maxPages = Math.ceil(totalItems / page.pageSize);

    if (
      event.currentTarget.scrollHeight - Math.ceil(event.currentTarget.scrollTop) - 1 <=
        event.currentTarget.clientHeight &&
      page.pageNumber < maxPages - 1 &&
      !state.loading
    )
      handleChangePage(page.pageNumber + 1);
  };

  useEffect(() => {
    if (selectedValue && !state.items.map((item) => item.id).includes(selectedValue?.id)) {
      setState((state) => ({
        ...state,
        items: [...state.items.filter((_item) => _item.id !== selectedValue.id), selectedValue],
      }));
    }
  }, [selectedValue, state.items]);

  return (
    <FormControl>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectedValue?.id ?? ""}
        label={label}
        onChange={handleChange}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          PaperProps: {
            style: {
              padding: "15px",
              maxHeight: 200,
            },
            onScroll: handleScroll,
          },
        }}
        renderValue={(value) => {
          const selectedItem = state.items.find((_item) => _item.id === value);

          return (
            <Typography
              style={{
                ...customInputStyle,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {selectedItem ? selectedItem[fieldProp] : ""}

              {taggedMenuOption &&
                taggedMenuOption.displayTagWhenSelected &&
                taggedMenuOption.id === selectedItem?.id &&
                taggedMenuOption?.tag}
            </Typography>
          );
        }}
      >
        {state.items.map((_item) => {
          return (
            <MenuItem
              value={_item.id}
              key={_item.id}
            >
              <Typography
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Grid style={{ display: "flex" }}>
                  {`${_item[fieldProp]}`}
                  <span style={{ fontSize: "12px" }}>
                    {`${
                      !!childFields &&
                      `(${childFields.map((childField) =>
                        childField.fieldProp ? _item[childField.field][childField.fieldProp] : _item[childField.field]
                      )})`
                    }`}
                  </span>
                </Grid>

                {taggedMenuOption && taggedMenuOption.id === _item.id && taggedMenuOption?.tag}
              </Typography>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default CustomFetchingDropdown;
